import { Text, Image, Flex, Link, Box, VStack, HStack } from "@chakra-ui/react";
import { ImmutableObject, useHookstate } from "@hookstate/core";
import { useMemo } from "react";
import { Comic } from "../../../types";
import { ComicFrequencyStatusBadge } from "../../cards/components/ComicFrequencyStatusBadge";
import { FreeRibbon } from "../../cards/components/FreeRibbon";
import { ComicReadingStyleBadge } from "../../cards/components/ComicReadingStyleBadge";
import { GCVerticalsAndOriginalsBadge } from "../../cards/components/GcVerticalsAndOriginalsBadge";
import { GCStaffPickAndExclusiveBadge } from "../../cards/components/GcStaffPickAndExclusiveBadge";
import { AddToListButton } from "../../lists/AddToListButton";
import { Trans } from "react-i18next";
import { SealCheckThickIcon } from "../../icons/SealCheckThickIcon";
import { NavigationTrigger } from "../../../screens/reader/types";
import { useNavigateToReaderWithContext } from "../../../screens/reader/hooks/useNavigateToReaderWithContext";
import { useIsOverlayOpen } from "../../../screens/reader/hooks/hookstate/useIsOverlayOpen";
import { useReleaseData } from "../../../screens/reader/hooks/hookstate/useReleaseData";
import { rootComponentsStore } from "../../../hookstate/rootComponentsStore";

interface ComicDetailsHeaderProps {
  comicData: ImmutableObject<Comic> | undefined;
  selectedLangId?: string;
}
export function ComicDetailsHeader({
  comicData,
  selectedLangId,
}: ComicDetailsHeaderProps) {
  const { toggleIsOverlayOpen } = useIsOverlayOpen();
  const { releaseData } = useReleaseData();
  const slideInMenuConfig = useHookstate(rootComponentsStore.slideInMenuConfig);
  const navigationContext = slideInMenuConfig.navigationContext;

  const fullNavigationContext = useMemo(() => {
    return {
      ...navigationContext.get(),
      comicToNavigate: comicData as Comic,
      trigger: comicData?.user_read_status
        ? NavigationTrigger.continueReadingButton
        : NavigationTrigger.startReadingButton,
    };
  }, [navigationContext, comicData]);

  const { goToCTAUrl } = useNavigateToReaderWithContext(fullNavigationContext);

  const shouldNavigate = releaseData?.comic_id !== comicData?.id;

  const handleCoverClick = () => {
    if (shouldNavigate) {
      goToCTAUrl();
    } else {
      toggleIsOverlayOpen();
    }
  };

  const selectedComicLang = comicData?.comic_langs?.find(
    (lang) => lang.lang_id === selectedLangId,
  );
  const countGold = selectedComicLang?.count_gold ?? comicData?.count_gold;
  const totalPublishedReleases =
    selectedComicLang?.total_published_releases ??
    comicData?.total_published_releases;
  return (
    <Flex gap="16px">
      <Box>
        <Image
          borderRadius="10px"
          border="1px solid"
          borderColor="transparent.white.10"
          width="130px"
          alt={`Thumbnail for comic ${comicData?.name}`}
          src={comicData?.image_small_url}
          cursor="pointer"
          onClick={handleCoverClick}
          _hover={{ opacity: 0.9 }}
        />
        {comicData?.is_free && (
          <FreeRibbon
            color="success.500"
            position="absolute"
            top={0}
            left="-1px"
          />
        )}
      </Box>
      <Flex flexDirection="column" justifyContent="space-between">
        <VStack gap="8px" alignItems="flex-start">
          <ComicFrequencyStatusBadge comic={comicData as Comic} />
          <Link
            aria-label={`Read ${comicData?.name}`}
            href={comicData?.url}
            pt="4px"
          >
            <Text
              fontSize="16px"
              fontWeight="700"
              lineHeight="20.8px"
              fontFamily="Roboto"
              color="neutral.200"
            >
              {comicData?.name}
            </Text>
          </Link>
          <Link
            aria-label={`View ${comicData?.artist?.roman_name}`}
            href={comicData?.artist?.url}
          >
            <Text
              fontFamily="Roboto"
              fontSize="12px"
              fontWeight="700"
              lineHeight="18px"
              color="neutral.400"
            >
              {comicData?.artist?.roman_name}
            </Text>
          </Link>
          {comicData?.id && (
            <AddToListButton
              entityType="comic"
              entityId={comicData.id}
              size="sm"
            />
          )}
          <GCVerticalsAndOriginalsBadge comicData={comicData as Comic} />
          <ComicReadingStyleBadge comic={comicData as Comic} />
          <GCStaffPickAndExclusiveBadge comicData={comicData as Comic} />
        </VStack>
        {countGold ? (
          <HStack gap="4px" alignItems="center">
            <SealCheckThickIcon boxSize="14px" color="blaze.blaze" />
            <Text
              fontFamily="Roboto"
              fontSize="12px"
              fontWeight="500px"
              color="neutral.200"
            >
              <Trans
                i18nKey={
                  countGold === totalPublishedReleases
                    ? "components.cards.freeWithGold"
                    : "components.slideInMenu.comicDetailsMenu.goldClarity"
                }
                values={{
                  count: countGold,
                }}
              />
            </Text>
          </HStack>
        ) : null}
      </Flex>
    </Flex>
  );
}
